import type { FC } from 'react';
import type { Maps, Coords } from 'google-map-react';
import GoogleMap from 'google-map-react';
import type { LocationMapMarkerProps } from './LocationMapMarker';
import LocationMapMarker from './LocationMapMarker';
import { getArchiProMapVariables, GOOGLE_MAP_STYLES } from './LocationConstant';
import type {
    LocationDataType,
    ContactLoaderData,
} from '@modules/professional/type/contact-page-types';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useIsSafari } from '@modules/root/hook';
import { useRootData } from '@modules/root';

interface LocationMapProps {
    zoom: number;
    center: Coords;
    filteredLocations: LocationDataType[];
    hoveredLocation?: LocationDataType;
    clickedLocation?: LocationDataType;
    onMapMarkerHover: (location: LocationDataType) => void;
    onMapMarkerClick: (location: LocationDataType, key?: number) => void;
    onMapPopupClose?: () => void;
    linkedProfiles: ContactLoaderData['linkedProfiles'];
    /**
     * The professional's country, used to pick the map's zoom/restriction
     * variables. Falls back to the site localisation country. Passed as a prop
     * so the map is not coupled to the professional-page route data and can be
     * reused elsewhere (e.g. the project detail "Get in touch" accordion).
     */
    professionalCountry?: string;
}
export const LocationMap: FC<LocationMapProps> = ({
    zoom,
    center,
    filteredLocations,
    hoveredLocation,
    clickedLocation,
    onMapMarkerHover,
    onMapMarkerClick,
    onMapPopupClose,
    linkedProfiles,
    professionalCountry,
}) => {
    const { config, localisation } = useRootData();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const isSafari = useIsSafari();

    const mapCountry = professionalCountry || localisation.country;
    const mapVariables = getArchiProMapVariables(mapCountry, isMobile);

    const onChildClick = (key: number, childProps: LocationMapMarkerProps) => {
        const location = filteredLocations.find(
            (m) => m.ID === childProps.locationID,
        );
        if (location) {
            onMapMarkerClick(location, key);
        }
    };

    const onChildMouseEnter = (
        key: number,
        childProps: LocationMapMarkerProps,
    ) => {
        const location = filteredLocations.find(
            (m) => m.ID === childProps.locationID,
        );
        if (hoveredLocation?.ID !== childProps.locationID && location) {
            if (isMobile && isSafari) {
                onMapMarkerClick(location, key);
            } else {
                onMapMarkerHover(location);
            }
        }
    };

    const mapOptions = (maps: Maps) => {
        return {
            styles: GOOGLE_MAP_STYLES,
            fullscreenControl: false,
            zoomControl: true,
            zoomControlOptions: {
                position: maps.ControlPosition.TOP_RIGHT,
            },
            minZoom: mapVariables.zoom.min,
            maxZoom: mapVariables.zoom.max,
            restriction: mapVariables.restriction,
            clickableIcons: false,
        };
    };
    return (
        <GoogleMap
            bootstrapURLKeys={{
                key: config.pixels.websiteGoogleMapKey,
                language: 'en',
                region: 'new zealand',
            }}
            center={center}
            zoom={zoom}
            options={mapOptions}
            onChildClick={onChildClick}
            onChildMouseEnter={onChildMouseEnter}
        >
            {filteredLocations.map(
                (locationData, index) =>
                    locationData?.ID &&
                    locationData.MapCoordinates && (
                        <LocationMapMarker
                            key={index}
                            lat={locationData.MapCoordinates.Latitude}
                            lng={locationData.MapCoordinates.Longitude}
                            locationID={locationData.ID}
                            hoveredLocation={hoveredLocation}
                            clickedLocation={clickedLocation}
                            onMapPopupClose={onMapPopupClose}
                            linkedProfiles={linkedProfiles}
                        />
                    ),
            )}
        </GoogleMap>
    );
};
